// The conversation transcript — ONE implementation for every surface that
// shows what was said (full VoiceOverlay, in-composer VoiceStrip, and the
// minimized island in SimoneReasoningPill). It previously existed twice,
// near-identically, and the strip would have made three (Rule 63); hosts now
// reference the bare key and override only what differs.
//
// SOURCE PRECEDENCE — deliberately NOT a branch on which engine is live:
//   1. `root.voiceTurns` — the engine-agnostic contract both engines write
//      ({ id, role: 'user' | 'agent' | 'action', text, streaming? }).
//   2. the assistant thread (`<voiceChatKey>chatMessages`) — the legacy source
//      the Web-Speech talk path routes through. Kept as a fallback so the log
//      never goes blank in the window before/while `voiceTurns` is populated,
//      and so a host that only has the thread still reads correctly.
// Surfaces must never ask WHICH engine is running: they render these rows.
//
// A host that needs a DIFFERENT set of rows (the island merges the assistant
// conversation into the same list so there is one thread to follow, rather
// than a voice log and a chat preview that replace each other) supplies a
// `turnsSource` factory. Deliberately not named `rows` — that is a real HTML
// attribute and DOMQL would mirror it onto the node.
//
// Why this exists at all: an ElevenLabs conversation produced NO text on any
// surface — the engine wrote a reply key nothing read, and the overlay log
// only ever derived from the assistant thread, which conversational turns
// never enter (2026-08-02 per Nika "it should also show texts").

export const VoiceLog = {
  // FA204 / frank trap (workspace d6ea1142, project_frank_drops_module_local_
  // helpers) — every helper below used to be a bare module-scope const,
  // read from inside `show`/`children`/`onRender`/`onUpdate`; undefined once
  // frank.toJSON'd for platform delivery. All are pure/stateless (no mutable
  // state lives here — see `stick` below for why the ONE thing that needed a
  // durable slot, the scroll signature, stays on `el`, not scope: scope is
  // the same shared object across every instance of a component, and the
  // three VoiceLog hosts — overlay, strip, island — would fight over one
  // signature if it lived here).
  scope: {
    // 'action' is a command-mode RESULT (the canvas actually did something)
    // rather than speech. It earns its own row kind so a single list can
    // still tell doing from talking (2026-08-02 per Nika "in command mode it
    // should also text the answers") — before this, a command flashed a ✓
    // and left no trace.
    norm(role) {
      return role === 'user' ? 'user' : role === 'action' ? 'action' : 'agent'
    },

    // Newest 14 turns, normalised and stable-keyed so DOMQL diffs rows in
    // place (that is what makes a streaming reply read as growing text
    // rather than a line being swapped out).
    rows(root) {
      const live = (root && root.voiceTurns) || []
      if (live.length) {
        return live
          .filter((t) => t && String(t.text || '').trim())
          .slice(-14)
          .map((t, i) => ({
            id: t.id || 'vt' + i,
            // `kind` wins over `role`: action turns are written as
            // { role: 'agent', kind: 'action' } (voiceSession _actionTurn),
            // so role-first evaluation rendered every executed command as
            // plain assistant speech and the 'action' branch below was
            // unreachable.
            role: this.norm(t.kind || t.role),
            text: t.text,
            streaming: !!t.streaming
          }))
      }
      const msgs = (root && root[(root.voiceChatKey || '') + 'chatMessages']) || []
      return msgs
        .filter(
          (m) => m && (m.role === 'user' || m.role === 'assistant') && String(m.text || '').trim()
        )
        .slice(-14)
        .map((m, i) => ({
          id: m.id || 'vm' + i,
          role: this.norm(m.role),
          text: m.text,
          streaming: false
        }))
    },

    // Resolve the host's override once per evaluation, falling back to the
    // shared source. Called from both `show` and `children` so they can
    // never disagree.
    resolve(el, s) {
      const src = el && el.turnsSource
      const out = typeof src === 'function' ? src(el, s) : src
      return out || this.rows(s && s.root)
    },

    // Stick to the newest turn — scroll this log's own node to its bottom
    // whenever a NEW turn appears or the streaming tail grows (2026-08-06
    // per Nika "always scroll at the bottom of voice thread when new
    // something pops"). Two parts:
    //
    //   • content signature (row count + last row's text length): onUpdate
    //     fires for EVERY root update — voiceStatus flips, interim text,
    //     unrelated app state — and yanking the scroll on churn that grew
    //     nothing would fight a user reading an older turn. Only actual
    //     thread growth scrolls. Stored on the element (the MessageList
    //     `el._roBottom` convention), NOT on scope — see the file-header
    //     note.
    //   • rAF before writing scrollTop — same reason as ChatMessageList: the
    //     hook can run before this update wave's child mutations lay out,
    //     and a scrollHeight read that early sticks to the PREVIOUS bottom,
    //     which is exactly the reported "does not follow new turns" bug.
    //
    // No near-bottom anti-yank check (deliberate divergence from
    // ChatMessageList): the ask is ALWAYS bottom on new content, and this is
    // a capped 14-row live transcript, not a paginated history the user
    // scrolls back through mid-turn.
    stick(el) {
      const rows = this.resolve(el, el.state)
      const last = rows.length ? rows[rows.length - 1] : null
      const sig = rows.length + ':' + (last ? String(last.text || '').length : 0)
      if (el._voiceLogSig === sig) return
      el._voiceLogSig = sig
      const node = el.node
      if (!node) return
      requestAnimationFrame(() => {
        if (!node.isConnected) return
        // `.scrollTo()` (a method call), not `.scrollTop =` (a property
        // assignment) — FA511 treats el.node as read-only from project
        // code; `.scrollTo()` is a sanctioned exception. No `behavior`, so
        // still the same instant jump as before — a safe swap, not a
        // rewrite. (The identical `X.scrollTop = X.scrollHeight` shape
        // exists elsewhere in chat/MessageList.js, chat/ThreadPanel.js and
        // shared/_simone/Chat.js — out of this ticket's scope, left as-is.)
        node.scrollTo(0, node.scrollHeight)
      })
    },

    // Returns a bundle KEY; the `Who` child resolves it at its render site.
    who(role) {
      if (role === 'user') return 'shell.voice.log.you'
      if (role === 'action') return 'shell.voice.log.done'
      return 'shell.voice.log.assistant'
    },

    // One accent, and only where it means something (brand: green = a thing
    // that actually happened). Speech stays monochrome so actions are the
    // only colour in the thread and read instantly; the speaker is quiet ink
    // either way — the line under it is what matters.
    whoColor(role) {
      return role === 'action' ? 'green' : 'caption'
    }
  },

  display: null,
  show: (el, s) => el.scope.resolve(el, s).length > 0,
  flow: 'y',
  gap: 'Z',
  width: '100%',
  maxHeight: 'F',
  overflow: 'hidden auto',
  textAlign: 'left',
  // CAPTIONS, not a log (2026-10-22, the call redesign): a quiet sentence-case
  // speaker over each line instead of a shouting uppercase eyebrow, and the
  // NEWEST line in the title ink so the eye lands on what was just said; the
  // older lines step back to the paragraph ink. Sizes are relative to the
  // host's own fontSize (the island reads at Z1, the call screen at A1) —
  // the type tokens are em-based, so one set serves every host.
  children: (el, s) => {
    const rows = el.scope.resolve(el, s)
    return rows.map((m, i) => ({
      key: m.id,
      flow: 'y',
      gap: 'W',
      Who: {
        tag: 'span',
        text: el.call('polyglot', el.scope.who(m.role)),
        fontSize: 'Z',
        fontWeight: '500',
        color: el.scope.whoColor(m.role)
      },
      Body: {
        tag: 'p',
        // The ellipsis is the only streaming affordance — a caret would need a
        // per-frame animation on a node the transcript rebuilds constantly.
        text: m.streaming ? m.text + '…' : m.text,
        fontSize: 'A',
        lineHeight: '1.45',
        color: i === rows.length - 1 ? 'title' : 'paragraph'
      }
    }))
  },
  onRender: (el) => el.scope.stick(el),
  onUpdate: (el) => el.scope.stick(el)
}
